import { z } from "zod";

import {
  AccountPrefixSchema,
  AuditActionSchema,
  CanvasKindSchema,
  CanvasModeSchema,
  CanvasStatusSchema,
  ClassStatusSchema,
  DisplayNameSchema,
  JsonValueSchema,
  LoginIdSchema,
  MAX_SCENE_BYTES,
  PasswordSchema,
  PrincipalSchema,
  RoleSchema,
  StudentNumberSchema,
  createSceneSchema,
  TitleSchema,
  UserStatusSchema,
  UuidSchema,
} from "./domain.js";
import type { CanvasCapability, ClassCapability } from "./domain.js";
import { MAX_GROUP_COUNT, MIN_GROUP_COUNT } from "./lesson.js";

export const ErrorCodeSchema = z.enum([
  "INVALID_CREDENTIALS",
  "RATE_LIMITED",
  "UNAUTHENTICATED",
  "CSRF_INVALID",
  "FORBIDDEN",
  "ACCOUNT_DISABLED",
  "NAME_REQUIRED",
  "CLASS_ARCHIVED",
  "CANVAS_TRASHED",
  "RESOURCE_NOT_FOUND",
  "VALIDATION_FAILED",
  "PREFIX_TAKEN",
  "LOGIN_ID_TAKEN",
  "CONFLICT",
  "STALE_EPOCH",
  "PERSISTENCE_FAILED",
  "ASSET_FAILED",
  "EMAIL_CODE_INVALID",
  "BETA_APPLICATION_EXISTS",
  "MAIL_UNAVAILABLE",
  "SCHOOL_NOT_FOUND",
  "FRAME_NOT_SEALED",
  "GALLERY_POST_BLOCKED",
  "SCHOOL_REQUIRED",
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const HttpErrorEnvelopeSchema = z
  .object({
    error: z
      .object({
        code: ErrorCodeSchema,
        message: z.string(),
        requestId: z.string().min(1),
        details: z.record(JsonValueSchema).optional(),
      })
      .strict(),
  })
  .strict();

export const EmptyObjectSchema = z.object({}).strict();
export const TransportHeadersSchema = z.object({}).passthrough();

export const UuidParamsSchema = z.object({ id: UuidSchema }).strict();
export const TeacherParamsSchema = z.object({ teacherId: UuidSchema }).strict();
export const ClassParamsSchema = z.object({ classId: UuidSchema }).strict();
export const StudentParamsSchema = z
  .object({ classId: UuidSchema, studentNumber: StudentNumberSchema })
  .strict();
export const CanvasParamsSchema = z
  .object({ classId: UuidSchema, canvasId: UuidSchema })
  .strict();
export const AssetParamsSchema = z
  .object({
    classId: UuidSchema,
    canvasId: UuidSchema,
    fileId: z.string().min(1).max(128),
  })
  .strict();
export const RevisionParamsSchema = z
  .object({
    classId: UuidSchema,
    canvasId: UuidSchema,
    revisionId: UuidSchema,
  })
  .strict();

export const CsrfHeadersSchema = z
  .object({ "x-csrf-token": z.string().min(32), origin: z.string().url() })
  .passthrough();
export const IdempotencyHeadersSchema = CsrfHeadersSchema.extend({
  "idempotency-key": UuidSchema,
}).passthrough();

export const LoginRequestSchema = z
  .object({ loginId: LoginIdSchema, password: PasswordSchema })
  .strict();

/** Teacher sign-in providers the server has credentials for. */
export const OAuthProviderSchema = z.enum(["GOOGLE", "NAVER"]);
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;

export const OAuthProviderListSchema = z
  .object({ providers: z.array(OAuthProviderSchema) })
  .strict();
export type OAuthProviderList = z.infer<typeof OAuthProviderListSchema>;

export const ChangePasswordRequestSchema = z
  .object({ currentPassword: PasswordSchema, newPassword: PasswordSchema })
  .strict()
  .refine((value) => value.currentPassword !== value.newPassword, {
    path: ["newPassword"],
    message: "New password must differ from current password",
  });

/** NEIS standard school code (SD_SCHUL_CODE). */
export const SchoolCodeSchema = z.string().regex(/^[0-9]{7}$/);
/** A school from the NEIS school list (imported into the `schools` table). */
export const SchoolSchema = z
  .object({
    code: SchoolCodeSchema,
    name: z.string().min(1),
    /** Education office name, e.g. 충청남도교육청. */
    officeName: z.string(),
    /** Province, e.g. 충청남도. */
    region: z.string(),
    /** School kind, e.g. 초등학교. */
    kind: z.string(),
    /** Road address; empty when NEIS has none. */
    address: z.string(),
  })
  .strict();
export const SchoolSearchQuerySchema = z
  .object({ q: z.string().trim().min(2).max(40) })
  .strict();
/** Up to 20 schools whose name contains `q`, exact name matches first. */
export const SchoolSearchResponseSchema = z
  .object({ schools: z.array(SchoolSchema).max(20) })
  .strict();
export const SchoolSetRequestSchema = z
  .object({ schoolCode: SchoolCodeSchema })
  .strict();

export const TeacherCreateRequestSchema = z
  .object({ loginId: LoginIdSchema })
  .strict();

export const TeacherStatusRequestSchema = z
  .object({ status: UserStatusSchema })
  .strict();

export const ClassCreateRequestSchema = z
  .object({
    name: TitleSchema,
    accountPrefix: AccountPrefixSchema,
    studentCount: z.number().int().min(1).max(30).default(30),
  })
  .strict();

export const ClassRenameRequestSchema = z
  .object({ name: TitleSchema })
  .strict();

export const UpdateGroupCountRequestSchema = z
  .object({
    groupCount: z.number().int().min(MIN_GROUP_COUNT).max(MAX_GROUP_COUNT),
  })
  .strict();

export const StudentAccountPatchRequestSchema = z
  .object({
    displayName: DisplayNameSchema.nullable().optional(),
    enabled: z.boolean().optional(),
  })
  .strict()
  .refine(
    (value) => value.displayName !== undefined || value.enabled !== undefined,
    { message: "At least one student account field is required" },
  );

export const DisplayNameRequestSchema = z
  .object({ displayName: DisplayNameSchema })
  .strict();

/** Title of the sample canvas the client creates with every new class.
 * Admin statistics leave canvases with this title out. */
export const SAMPLE_CANVAS_TITLE = "예시 캔버스 · 모둠 활동 네 가지";

export const CanvasCreateRequestSchema = z
  .object({
    title: TitleSchema,
    /** PRESENTATION makes a presentation lesson (발표 수업); it takes no
     * scene, since each student's slides live in their own child canvas. */
    template: z
      .enum(["NONE", "INDIVIDUAL", "GROUP", "PRESENTATION"])
      .optional(),
    scene: z.array(z.record(z.unknown())).optional(),
  })
  .strict();
export const CanvasRenameRequestSchema = z
  .object({ title: TitleSchema })
  .strict();
export const CanvasDuplicateRequestSchema = z
  .object({ name: TitleSchema })
  .strict();

export const RevisionRestoreRequestSchema = z
  .object({
    expectedEpoch: UuidSchema,
    expectedVersion: z.number().int().min(0),
  })
  .strict();

/**
 * What a signed-in browser tells operators: an uncaught error, a typed
 * report, or a survey answer. `detail` carries recent client events,
 * identifiers and the browser; never drawing content.
 */
export const ClientReportSourceSchema = z.enum([
  "ERROR",
  "USER_REPORT",
  "SURVEY",
]);
export const ClientReportRequestSchema = z
  .object({
    source: ClientReportSourceSchema,
    kind: z.string().max(80).optional(),
    message: z.string().trim().min(1).max(2000),
    classId: UuidSchema.optional(),
    canvasId: UuidSchema.optional(),
    detail: z
      .record(JsonValueSchema)
      .refine((value) => JSON.stringify(value).length <= 20_000)
      .optional(),
  })
  .strict();
export type ClientReportSource = z.infer<typeof ClientReportSourceSchema>;
export type ClientReportRequest = z.infer<typeof ClientReportRequestSchema>;

/** What the signed-in user wrote through 의견·요청 or the survey, newest
 * first, for as long as the 30-day retention keeps it. */
export const MyClientReportSchema = z
  .object({
    id: z.string(),
    source: z.enum(["USER_REPORT", "SURVEY"]),
    message: z.string(),
    createdAt: z.string().datetime(),
    /** The operator's answer, once there is one. */
    reply: z.string().nullable(),
    repliedAt: z.string().datetime().nullable(),
  })
  .strict();
export const MyClientReportListSchema = z
  .object({ items: z.array(MyClientReportSchema) })
  .strict();
export type MyClientReport = z.infer<typeof MyClientReportSchema>;

/**
 * What the header bell lists: for now only an answer to the user's own
 * report. Read when a page opens, never pushed.
 */
export const NotificationSchema = z
  .object({
    id: z.string(),
    kind: z.literal("REPORT_REPLY"),
    createdAt: z.string().datetime(),
    readAt: z.string().datetime().nullable(),
    report: z
      .object({
        id: z.string(),
        message: z.string(),
        reply: z.string(),
        repliedAt: z.string().datetime(),
      })
      .strict(),
  })
  .strict();
export const NotificationListSchema = z
  .object({
    items: z.array(NotificationSchema),
    unreadCount: z.number().int().nonnegative(),
  })
  .strict();
export const NotificationReadRequestSchema = z
  .object({
    ids: z
      .array(z.string().regex(/^[0-9]{1,18}$/))
      .min(1)
      .max(100),
  })
  .strict();
export type Notification = z.infer<typeof NotificationSchema>;
export type NotificationList = z.infer<typeof NotificationListSchema>;

/**
 * Email ownership check by a mailed six-digit code. `purpose` keeps codes
 * for different flows apart; teacher sign-up will add its own value.
 */
export const EmailSchema = z
  .string()
  .transform((value) => value.trim().toLowerCase())
  .pipe(z.string().email().max(254));
export const EmailVerificationPurposeSchema = z.enum(["BETA_APPLICATION"]);
export const EmailVerificationCodeSchema = z.string().regex(/^[0-9]{6}$/);
export const EmailVerificationRequestSchema = z
  .object({ email: EmailSchema, purpose: EmailVerificationPurposeSchema })
  .strict();

/** An open-beta application: sent with the mailed code, decided by an administrator. */
export const BetaApplicationStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
]);
export const BetaApplicationRequestSchema = z
  .object({
    email: EmailSchema,
    code: EmailVerificationCodeSchema,
    name: z.string().trim().min(2).max(40),
    schoolCode: SchoolCodeSchema,
    message: z.string().trim().max(1000).optional(),
    consent: z.literal(true),
  })
  .strict();
export const BetaApplicationParamsSchema = z
  .object({ applicationId: UuidSchema })
  .strict();
export const BetaApplicationSchema = z
  .object({
    id: UuidSchema,
    email: z.string(),
    name: z.string(),
    /** NEIS school name for new applications; free text on older ones. */
    schoolName: z.string(),
    /** Null on applications made before the school picker. */
    schoolCode: SchoolCodeSchema.nullable(),
    message: z.string(),
    status: BetaApplicationStatusSchema,
    /** The account created on approval. */
    teacherLoginId: LoginIdSchema.nullable(),
    /** When the sign-in mail last left; null while it has not. */
    credentialsSentAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable(),
  })
  .strict();
/** `q` matches name, email and school; `cursor` is opaque (issued by the server). */
export const BetaApplicationListQuerySchema = z
  .object({
    status: BetaApplicationStatusSchema.optional(),
    q: z.string().trim().min(1).max(100).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
/**
 * `counts` honours the `q` filter but never the `status` filter, so the tab
 * badges stay stable while a tab is selected.
 */
export const BetaApplicationListSchema = z
  .object({
    items: z.array(BetaApplicationSchema),
    nextCursor: z.string().min(1).nullable(),
    counts: z
      .object({
        pending: z.number().int().min(0),
        approved: z.number().int().min(0),
        rejected: z.number().int().min(0),
      })
      .strict(),
  })
  .strict();
export type EmailVerificationPurpose = z.infer<
  typeof EmailVerificationPurposeSchema
>;
export type EmailVerificationRequest = z.infer<
  typeof EmailVerificationRequestSchema
>;
export type BetaApplicationStatus = z.infer<typeof BetaApplicationStatusSchema>;
export type BetaApplicationRequest = z.infer<
  typeof BetaApplicationRequestSchema
>;
export type BetaApplication = z.infer<typeof BetaApplicationSchema>;
export type BetaApplicationListQuery = z.infer<
  typeof BetaApplicationListQuerySchema
>;
export type BetaApplicationList = z.infer<typeof BetaApplicationListSchema>;

/**
 * The token in an unsubscribe link of a notice or lesson-tips mail: `u` is
 * the SHA-256 hex of the normalized address, `s` its signature. The token is
 * the whole authority; no session or origin is involved.
 */
export const MailUnsubscribeRequestSchema = z
  .object({
    u: z.string().regex(/^[0-9a-f]{64}$/),
    s: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
  })
  .strict();
export type MailUnsubscribeRequest = z.infer<
  typeof MailUnsubscribeRequestSchema
>;

export const CursorQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();

/** Administrator teacher search: `q` matches login id and email. */
export const TeacherListQuerySchema = CursorQuerySchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
}).strict();

export const ClassListQuerySchema = CursorQuerySchema.extend({
  status: z.enum(["ACTIVE", "ARCHIVED", "ALL"]).default("ACTIVE"),
}).strict();

export const IdempotencyKeySchema = UuidSchema;

export const StudentAccountSchema = z
  .object({
    userId: UuidSchema,
    classId: UuidSchema,
    studentNumber: StudentNumberSchema,
    loginId: LoginIdSchema,
    displayName: DisplayNameSchema.nullable(),
    enabled: z.boolean(),
    initialPassword: z.literal("12345678").nullable(),
    lastLoginAt: z.string().datetime().nullable(),
  })
  .strict();

export const TeacherSchema = z
  .object({
    id: UuidSchema,
    loginId: LoginIdSchema,
    status: UserStatusSchema,
    /** Profile email; only teachers created by beta approval have one. */
    email: z.string().nullable(),
    initialPasswordActive: z.boolean(),
    school: SchoolSchema.nullable(),
    /** School as typed on the beta application, for linking by hand. */
    applicationSchoolName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export const ClassSchema = z
  .object({
    id: UuidSchema,
    teacherUserId: UuidSchema,
    name: TitleSchema,
    accountPrefix: AccountPrefixSchema,
    status: ClassStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    groupCount: z.number().int().min(MIN_GROUP_COUNT).max(MAX_GROUP_COUNT),
  })
  .strict();

export const CanvasSchema = z
  .object({
    id: UuidSchema,
    classId: UuidSchema,
    title: TitleSchema,
    status: CanvasStatusSchema,
    trashExpiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    kind: CanvasKindSchema,
    /** The presentation lesson a PRESENTATION_STUDENT canvas belongs to;
     * null for every other kind. */
    parentCanvasId: UuidSchema.nullable(),
    /** The student whose slides these are (PRESENTATION_STUDENT only). */
    ownerStudentNumber: StudentNumberSchema.nullable(),
    /** When that student checked 완료; null while unchecked and for every
     * other kind. Completion never locks editing. */
    completedAt: z.string().datetime().nullable(),
  })
  .strict();

export const PresentationStudentParamsSchema = z
  .object({
    classId: UuidSchema,
    canvasId: UuidSchema,
    studentNumber: StudentNumberSchema,
  })
  .strict();

export const CanvasCompletionRequestSchema = z
  .object({ completed: z.boolean() })
  .strict();

/** One student card on the teacher's presentation lesson screen. */
export const PresentationStudentSchema = z
  .object({
    studentNumber: StudentNumberSchema,
    displayName: DisplayNameSchema.nullable(),
    enabled: z.boolean(),
    /** Null until the student or the teacher first opens it. */
    canvasId: UuidSchema.nullable(),
    /** Frames (slides) in the student's canvas; 0 before it exists. */
    slideCount: z.number().int().min(0),
    completedAt: z.string().datetime().nullable(),
    /** Last change to the student's canvas; null before it exists. */
    updatedAt: z.string().datetime().nullable(),
  })
  .strict();

export const PresentationStudentListSchema = z
  .object({ items: z.array(PresentationStudentSchema) })
  .strict();

export const createCanvasStateSchema = (maximumBytes = MAX_SCENE_BYTES) =>
  z
    .object({
      epoch: UuidSchema,
      version: z.number().int().min(0),
      scene: createSceneSchema(maximumBytes),
      assetIds: z.array(z.string().min(1).max(128)),
      mode: CanvasModeSchema,
    })
    .strict();

export const CanvasStateSchema = createCanvasStateSchema();

export const IMAGE_MIME_TYPES = [
  "image/svg+xml",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/x-icon",
  "image/avif",
  "image/jfif",
] as const;

export const ImageMimeTypeSchema = z.enum(IMAGE_MIME_TYPES);

export const AssetSchema = z
  .object({
    fileId: z.string().min(1).max(128),
    mimeType: ImageMimeTypeSchema,
    byteSize: z.number().int().min(1).max(4_194_304),
    createdAt: z.string().datetime(),
    etag: z.string().min(1),
  })
  .strict();

export const AuthSessionEnvelopeSchema = z
  .object({
    principal: PrincipalSchema,
    studentProfile: StudentAccountSchema.omit({
      initialPassword: true,
    }).optional(),
    /** Present exactly when the principal is a teacher. */
    teacherProfile: z
      .object({ school: SchoolSchema.nullable() })
      .strict()
      .optional(),
    /**
     * Present exactly when an administrator entered this teacher's account
     * from the teacher list; the principal is then the teacher.
     */
    impersonator: z
      .object({ userId: UuidSchema, loginId: LoginIdSchema })
      .strict()
      .optional(),
    csrfToken: z.string().min(32),
    expiresAt: z.string().datetime(),
    initialPasswordWarning: z.boolean(),
  })
  .strict();

export const SessionRefreshEnvelopeSchema = z
  .object({ csrfToken: z.string().min(32), expiresAt: z.string().datetime() })
  .strict();

export const RevisionSourceSchema = z.enum([
  "AUTO_INTERVAL",
  "AUTO_IDLE",
  "PRE_RESTORE",
  "RESTORE_RESULT",
  "MANUAL",
]);

export const RevisionSchema = z
  .object({
    id: UuidSchema,
    canvasId: UuidSchema,
    epoch: UuidSchema,
    version: z.number().int().min(0),
    source: RevisionSourceSchema,
    actorUserId: UuidSchema.nullable(),
    restoredRevisionId: UuidSchema.nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

const createPageSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z
    .object({
      items: z.array(itemSchema),
      nextCursor: z.string().min(1).nullable(),
    })
    .strict();

/**
 * Administrator dashboard. Administrators never see class or canvas content,
 * so none of these carry names, titles or content (the audit events'
 * `targetId` uuid is the one exception).
 */
const CountSchema = z.number().int().min(0);
export const AdminOverviewSchema = z
  .object({
    teachers: z
      .object({
        active: CountSchema,
        disabled: CountSchema,
        initialPassword: CountSchema,
      })
      .strict(),
    beta: z
      .object({
        pending: CountSchema,
        approved: CountSchema,
        rejected: CountSchema,
        credentialsUnsent: CountSchema,
      })
      .strict(),
    classes: z.object({ active: CountSchema, archived: CountSchema }).strict(),
    students: z.object({ enabled: CountSchema }).strict(),
    canvases: z.object({ active: CountSchema }).strict(),
    reports7d: z
      .object({
        error: CountSchema,
        userReport: CountSchema,
        survey: CountSchema,
        diagnostic: CountSchema,
      })
      .strict(),
  })
  .strict();

/**
 * Teacher usage overview: login ids, applicant names and schools (already on
 * the teacher screens) plus counts. Class and canvas names never appear.
 */
export const AdminTeacherActivitySchema = z
  .object({
    generatedAt: z.string().datetime(),
    funnel: z
      .object({
        teachers: CountSchema,
        credentialsSent: CountSchema,
        loggedIn: CountSchema,
        passwordChanged: CountSchema,
        classCreated: CountSchema,
        studentsNamed: CountSchema,
        canvasCreated: CountSchema,
        studentsLoggedIn: CountSchema,
        canvasEdited: CountSchema,
        active7d: CountSchema,
      })
      .strict(),
    teachers: z.array(
      z
        .object({
          /** For entering the teacher's screen from this list. */
          id: UuidSchema,
          loginId: z.string().min(1),
          name: z.string().nullable(),
          schoolName: z.string().nullable(),
          status: UserStatusSchema,
          createdAt: z.string().datetime(),
          credentialsSentAt: z.string().datetime().nullable(),
          firstLoginAt: z.string().datetime().nullable(),
          lastLoginAt: z.string().datetime().nullable(),
          passwordChanged: z.boolean(),
          classes: CountSchema,
          students: CountSchema,
          studentsNamed: CountSchema,
          studentsLoggedIn: CountSchema,
          canvases: CountSchema,
          canvasesEdited: CountSchema,
          lastStudentLoginAt: z.string().datetime().nullable(),
          /**
           * When the teacher first reached each funnel step, for the chart
           * over time. Null exactly when the step is not reached now (so the
           * running total ends at today's funnel count); some times are
           * approximations from what the database still keeps.
           */
          reachedAt: z
            .object({
              teachers: z.string().datetime(),
              credentialsSent: z.string().datetime().nullable(),
              loggedIn: z.string().datetime().nullable(),
              passwordChanged: z.string().datetime().nullable(),
              classCreated: z.string().datetime().nullable(),
              studentsNamed: z.string().datetime().nullable(),
              canvasCreated: z.string().datetime().nullable(),
              studentsLoggedIn: z.string().datetime().nullable(),
              canvasEdited: z.string().datetime().nullable(),
            })
            .strict(),
        })
        .strict(),
    ),
    /** Internal (operator and test) teacher accounts left out of everything above. */
    internalExcluded: CountSchema,
  })
  .strict();

/**
 * Live server view for the operator: process-memory metrics and the open
 * rooms. Rooms carry the teacher's login id and head counts only; class and
 * canvas ids, names and student names never appear.
 */
const ReportCountsSchema = z
  .object({
    error: CountSchema,
    userReport: CountSchema,
    survey: CountSchema,
    diagnostic: CountSchema,
  })
  .strict();
export const AdminLiveSchema = z
  .object({
    generatedAt: z.string().datetime(),
    process: z
      .object({
        startedAt: z.string().datetime(),
        ready: z.boolean(),
        rssBytes: CountSchema,
        eventLoopLagP99Seconds: z.number().min(0),
      })
      .strict(),
    realtime: z
      .object({
        connections: CountSchema,
        editors: CountSchema,
        rooms: CountSchema,
        pendingOperations: CountSchema,
      })
      .strict(),
    /** Seconds the `recent` figures below actually cover. */
    windowSeconds: CountSchema,
    signals: z.array(
      z
        .object({
          key: z.string().min(1),
          metric: z.string().min(1),
          label: z.string().nullable(),
          recent: CountSchema,
          total: CountSchema,
        })
        .strict(),
    ),
    latency: z
      .object({
        roomUpdateP95Seconds: z.number().min(0).nullable(),
        operationLogCommitP95Seconds: z.number().min(0).nullable(),
      })
      .strict(),
    reports: z
      .object({ last10m: ReportCountsSchema, last1h: ReportCountsSchema })
      .strict(),
    rooms: z.array(
      z
        .object({
          teacherLoginId: z.string().nullable(),
          teacherPresent: z.boolean(),
          students: CountSchema,
          activeStudents: CountSchema,
          handsRaised: CountSchema,
        })
        .strict(),
    ),
  })
  .strict();

/** bigint ids and cursors travel as decimal strings; 18 digits stay inside bigint. */
const BigintStringSchema = z.string().regex(/^[0-9]{1,18}$/);
const AdminFeedQueryShape = {
  cursor: BigintStringSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
};

export const AdminAuditEventSchema = z
  .object({
    id: BigintStringSchema,
    createdAt: z.string().datetime(),
    action: AuditActionSchema,
    targetType: z.string().min(1).max(64),
    targetId: UuidSchema.nullable(),
    actorLoginId: z.string().nullable(),
    targetLoginId: z.string().nullable(),
    metadata: z.record(JsonValueSchema),
  })
  .strict();
export const AdminAuditEventQuerySchema = z
  .object({ action: AuditActionSchema.optional(), ...AdminFeedQueryShape })
  .strict();
export const AdminAuditEventPageSchema = createPageSchema(
  AdminAuditEventSchema,
);

/** Browsers also file DIAGNOSTIC reports on their own; only operators read them. */
export const AdminClientReportSourceSchema = z.enum([
  "DIAGNOSTIC",
  ...ClientReportSourceSchema.options,
]);
export const AdminClientReportSchema = z
  .object({
    id: BigintStringSchema,
    createdAt: z.string().datetime(),
    source: AdminClientReportSourceSchema,
    kind: z.string(),
    message: z.string(),
    userLoginId: z.string().nullable(),
    userRole: RoleSchema.nullable(),
    userAgent: z.string().nullable(),
    reply: z.string().nullable(),
    repliedAt: z.string().datetime().nullable(),
  })
  .strict();
/** Answers a 의견·요청 or survey answer; the sender is notified. */
export const AdminClientReportReplyRequestSchema = z
  .object({ message: z.string().trim().min(1).max(2000) })
  .strict();
export const AdminClientReportQuerySchema = z
  .object({
    source: AdminClientReportSourceSchema.optional(),
    ...AdminFeedQueryShape,
  })
  .strict();
export const AdminClientReportPageSchema = createPageSchema(
  AdminClientReportSchema,
);
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;
export type AdminTeacherActivity = z.infer<typeof AdminTeacherActivitySchema>;
export type AdminLive = z.infer<typeof AdminLiveSchema>;
export type AdminAuditEvent = z.infer<typeof AdminAuditEventSchema>;
export type AdminAuditEventQuery = z.infer<typeof AdminAuditEventQuerySchema>;
export type AdminAuditEventPage = z.infer<typeof AdminAuditEventPageSchema>;
export type AdminClientReportSource = z.infer<
  typeof AdminClientReportSourceSchema
>;
export type AdminClientReport = z.infer<typeof AdminClientReportSchema>;
export type AdminClientReportQuery = z.infer<
  typeof AdminClientReportQuerySchema
>;
export type AdminClientReportPage = z.infer<typeof AdminClientReportPageSchema>;
export type TeacherListQuery = z.infer<typeof TeacherListQuerySchema>;

export const TeacherPageSchema = createPageSchema(TeacherSchema);
export const ClassPageSchema = createPageSchema(ClassSchema);
export const CanvasPageSchema = createPageSchema(CanvasSchema);
export const RevisionPageSchema = createPageSchema(RevisionSchema);
export const RosterEnvelopeSchema = z
  .object({ items: z.array(StudentAccountSchema).min(1).max(30) })
  .strict();
export const ClassCreateEnvelopeSchema = z
  .object({
    class: ClassSchema,
    roster: z.array(StudentAccountSchema).min(1).max(30),
  })
  .strict();
export const RevisionPreviewEnvelopeSchema = z
  .object({
    revision: RevisionSchema,
    scene: createSceneSchema(MAX_SCENE_BYTES),
    assetIds: z.array(z.string().min(1).max(128)),
  })
  .strict();
export const RevisionRestoreEnvelopeSchema = z
  .object({
    epoch: UuidSchema,
    version: z.number().int().min(0),
    scene: createSceneSchema(MAX_SCENE_BYTES),
  })
  .strict();
export const HealthLiveEnvelopeSchema = z
  .object({ status: z.literal("ok") })
  .strict();
export const HealthReadyOkEnvelopeSchema = z
  .object({ status: z.literal("ok") })
  .strict();
export const HealthReadyUnavailableEnvelopeSchema = z
  .object({ status: z.literal("unavailable") })
  .strict();
export const HealthReadyEnvelopeSchema = z.union([
  HealthReadyOkEnvelopeSchema,
  HealthReadyUnavailableEnvelopeSchema,
]);
export const HEALTH_READY_RESPONSE_BY_STATUS = {
  200: HealthReadyOkEnvelopeSchema,
  503: HealthReadyUnavailableEnvelopeSchema,
} as const;

export const AssetUploadHeadersSchema = CsrfHeadersSchema.extend({
  "content-type": ImageMimeTypeSchema,
}).passthrough();

const UndefinedSchema = z.undefined();
const BinaryBodySchema = z.instanceof(Uint8Array);

/*
 * 학교 밖 갤러리 (docs/superpowers/specs/2026-10-20-school-gallery-design.md).
 * A post is one image of a student's sealed personal frame. Cards never carry
 * who posted it: only the school and the class.
 */
export const GALLERY_REACTION_KINDS = [
  "LIKE",
  "AWESOME",
  "COLOR",
  "IDEA",
  "FUNNY",
] as const;
export const GalleryReactionKindSchema = z.enum(GALLERY_REACTION_KINDS);
/**
 * PUBLISHED is the only status anyone outside the class sees. WITHDRAWN: the
 * student took it down (or the teacher allowed the frame again).
 * REMOVED_BY_TEACHER blocks the frame until the teacher allows it again.
 * HIDDEN: reports hid it until an administrator decides. DELETED: an
 * administrator removed it for good.
 */
export const GalleryPostStatusSchema = z.enum([
  "PUBLISHED",
  "WITHDRAWN",
  "REMOVED_BY_TEACHER",
  "HIDDEN",
  "DELETED",
]);
export const GalleryReportReasonSchema = z.enum([
  "PERSONAL_INFO",
  "INAPPROPRIATE",
  "OTHER",
]);
export const GALLERY_IMAGE_MIME_TYPES = ["image/png", "image/webp"] as const;
export const GalleryImageMimeTypeSchema = z.enum(GALLERY_IMAGE_MIME_TYPES);
export const GALLERY_IMAGE_MAX_BYTES = 4_194_304;
export const GALLERY_IMAGE_MAX_SIDE = 2400;
/** An Excalidraw element id. */
export const FrameIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

export const GalleryReactionCountsSchema = z
  .object({
    LIKE: CountSchema,
    AWESOME: CountSchema,
    COLOR: CountSchema,
    IDEA: CountSchema,
    FUNNY: CountSchema,
  })
  .strict();
export const GalleryReactionStateSchema = z
  .object({
    reactions: GalleryReactionCountsSchema,
    /** The kinds the viewer has pressed. */
    myReactions: z.array(GalleryReactionKindSchema),
  })
  .strict();
const GallerySchoolLabelSchema = z
  .object({
    code: SchoolCodeSchema,
    name: z.string().min(1),
    officeName: z.string(),
  })
  .strict();
export const GalleryPostCardSchema = z
  .object({
    id: UuidSchema,
    school: GallerySchoolLabelSchema,
    classId: UuidSchema,
    /** The class's current name. */
    className: z.string(),
    width: z.number().int().min(1).max(GALLERY_IMAGE_MAX_SIDE),
    height: z.number().int().min(1).max(GALLERY_IMAGE_MAX_SIDE),
    createdAt: z.string().datetime(),
    /** Last time the image was replaced by posting the frame again. */
    updatedAt: z.string().datetime(),
    reportedByMe: z.boolean(),
    reactions: GalleryReactionCountsSchema,
    myReactions: z.array(GalleryReactionKindSchema),
  })
  .strict();
/**
 * Unfiltered lists sort by `sort`; with `schoolCode` or `classId` the order is
 * always newest first. `cursor` is opaque (issued by the server).
 */
export const GalleryPostListQuerySchema = z
  .object({
    sort: z.enum(["recent", "popular"]).default("recent"),
    schoolCode: SchoolCodeSchema.optional(),
    classId: UuidSchema.optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(48).default(24),
  })
  .strict();
export const GalleryPostPageSchema = z
  .object({
    items: z.array(GalleryPostCardSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export const GalleryPostParamsSchema = z
  .object({ postId: UuidSchema })
  .strict();
export const GalleryReactionParamsSchema = z
  .object({ postId: UuidSchema, kind: GalleryReactionKindSchema })
  .strict();
export const GalleryReportRequestSchema = z
  .object({ reason: GalleryReportReasonSchema })
  .strict();
/** Schools that have visible posts, matched like the school search. */
export const GallerySchoolListSchema = z
  .object({
    schools: z
      .array(SchoolSchema.extend({ postCount: CountSchema }).strict())
      .max(20),
  })
  .strict();
export const GallerySchoolParamsSchema = z
  .object({ schoolCode: SchoolCodeSchema })
  .strict();
/** Classes of one school that have visible posts. */
export const GalleryClassListSchema = z
  .object({
    classes: z.array(
      z
        .object({
          classId: UuidSchema,
          className: z.string(),
          postCount: CountSchema,
        })
        .strict(),
    ),
  })
  .strict();

export const FrameGalleryParamsSchema = z
  .object({
    classId: UuidSchema,
    canvasId: UuidSchema,
    frameId: FrameIdSchema,
  })
  .strict();
export const GalleryImageUploadHeadersSchema = CsrfHeadersSchema.extend({
  "content-type": GalleryImageMimeTypeSchema,
}).passthrough();
/** One post as seen inside its own class (by its student or teacher). */
export const ClassGalleryPostSchema = z
  .object({
    id: UuidSchema,
    canvasId: UuidSchema,
    frameId: FrameIdSchema,
    studentNumber: StudentNumberSchema,
    status: GalleryPostStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    reactions: GalleryReactionCountsSchema,
  })
  .strict();
/**
 * Posts of one canvas: a student sees their own, the teacher sees all. The
 * editor uses it to show each frame's 자랑하기 state.
 */
export const CanvasGalleryPostListSchema = z
  .object({ items: z.array(ClassGalleryPostSchema) })
  .strict();
/** The teacher's 「갤러리에 올라간 우리 반 작품」. */
export const TeacherClassGalleryPostSchema = ClassGalleryPostSchema.extend({
  canvasTitle: z.string(),
  displayName: DisplayNameSchema.nullable(),
  reportCount: CountSchema,
}).strict();
export const TeacherClassGalleryListSchema = z
  .object({
    items: z.array(TeacherClassGalleryPostSchema),
    /** Published since the teacher last marked the list seen. */
    unseenCount: CountSchema,
  })
  .strict();

export const AdminGalleryPostListQuerySchema = z
  .object({
    /** REPORTED: any status with at least one report. */
    filter: z.enum(["REPORTED", "HIDDEN", "ALL"]).default("REPORTED"),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export const AdminGalleryPostSchema = z
  .object({
    id: UuidSchema,
    school: GallerySchoolLabelSchema,
    classId: UuidSchema,
    className: z.string(),
    teacherLoginId: LoginIdSchema,
    status: GalleryPostStatusSchema,
    reports: z
      .object({
        PERSONAL_INFO: CountSchema,
        INAPPROPRIATE: CountSchema,
        OTHER: CountSchema,
      })
      .strict(),
    reactions: GalleryReactionCountsSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export const AdminGalleryPostPageSchema = z
  .object({
    items: z.array(AdminGalleryPostSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

const route = <T>(contract: T): T => contract;

export const HttpRouteContracts = {
  "POST /api/v1/auth/login": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: z.object({ origin: z.string().url() }).passthrough(),
    body: LoginRequestSchema,
    response: AuthSessionEnvelopeSchema,
    capability: null,
  }),
  "GET /api/v1/auth/session": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AuthSessionEnvelopeSchema,
    capability: null,
  }),
  "POST /api/v1/auth/session/refresh": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: SessionRefreshEnvelopeSchema,
    capability: null,
  }),
  "POST /api/v1/auth/logout": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "POST /api/v1/auth/change-password": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: ChangePasswordRequestSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "GET /api/v1/admin/teachers": route({
    params: EmptyObjectSchema,
    query: TeacherListQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: TeacherPageSchema,
    capability: null,
  }),
  "POST /api/v1/admin/teachers": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: TeacherCreateRequestSchema,
    response: TeacherSchema,
    capability: null,
  }),
  "PATCH /api/v1/admin/teachers/:teacherId/status": route({
    params: TeacherParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: TeacherStatusRequestSchema,
    response: TeacherSchema,
    capability: null,
  }),
  "POST /api/v1/admin/teachers/:teacherId/reset-password": route({
    params: TeacherParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "POST /api/v1/admin/teachers/:teacherId/impersonate": route({
    params: TeacherParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: AuthSessionEnvelopeSchema,
    capability: null,
  }),
  "POST /api/v1/auth/impersonation/end": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "GET /api/v1/auth/oauth/providers": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: OAuthProviderListSchema,
    capability: null,
  }),
  "POST /api/v1/email-verifications": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: z.object({ origin: z.string().url() }).passthrough(),
    body: EmailVerificationRequestSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "GET /api/v1/schools": route({
    params: EmptyObjectSchema,
    query: SchoolSearchQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: SchoolSearchResponseSchema,
    capability: null,
  }),
  "PUT /api/v1/admin/teachers/:teacherId/school": route({
    params: TeacherParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: SchoolSetRequestSchema,
    response: TeacherSchema,
    capability: null,
  }),
  "POST /api/v1/beta-applications": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: z.object({ origin: z.string().url() }).passthrough(),
    body: BetaApplicationRequestSchema,
    response: EmptyObjectSchema,
    capability: null,
  }),
  "GET /api/v1/admin/beta-applications": route({
    params: EmptyObjectSchema,
    query: BetaApplicationListQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: BetaApplicationListSchema,
    capability: null,
  }),
  "POST /api/v1/admin/beta-applications/:applicationId/approve": route({
    params: BetaApplicationParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: BetaApplicationSchema,
    capability: null,
  }),
  "POST /api/v1/admin/beta-applications/:applicationId/reject": route({
    params: BetaApplicationParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: BetaApplicationSchema,
    capability: null,
  }),
  "POST /api/v1/admin/beta-applications/:applicationId/resend": route({
    params: BetaApplicationParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: BetaApplicationSchema,
    capability: null,
  }),
  "DELETE /api/v1/admin/beta-applications/:applicationId": route({
    params: BetaApplicationParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: UndefinedSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  /**
   * The page sends the token as JSON. Mail clients doing RFC 8058 one-click
   * instead POST to `?u=…&s=…` with the form body `List-Unsubscribe=One-Click`,
   * which the route accepts in place of this body.
   */
  "POST /api/v1/mail/unsubscribe": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: MailUnsubscribeRequestSchema,
    response: EmptyObjectSchema,
    capability: null,
  }),
  "GET /api/v1/admin/overview": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminOverviewSchema,
    capability: null,
  }),
  "GET /api/v1/admin/teacher-activity": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminTeacherActivitySchema,
    capability: null,
  }),
  "GET /api/v1/admin/live": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminLiveSchema,
    capability: null,
  }),
  "GET /api/v1/admin/audit-events": route({
    params: EmptyObjectSchema,
    query: AdminAuditEventQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminAuditEventPageSchema,
    capability: null,
  }),
  "GET /api/v1/admin/client-reports": route({
    params: EmptyObjectSchema,
    query: AdminClientReportQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminClientReportPageSchema,
    capability: null,
  }),
  "GET /api/v1/classes": route({
    params: EmptyObjectSchema,
    query: ClassListQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: ClassPageSchema,
    capability: "CLASS_READ",
  }),
  "POST /api/v1/classes": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: ClassCreateRequestSchema,
    response: ClassCreateEnvelopeSchema,
    capability: null,
  }),
  "GET /api/v1/classes/:classId": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: ClassSchema,
    capability: "CLASS_READ",
  }),
  "PATCH /api/v1/classes/:classId": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: ClassRenameRequestSchema,
    response: ClassSchema,
    capability: "CLASS_MANAGE",
  }),
  "POST /api/v1/classes/:classId/archive": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: EmptyObjectSchema,
    response: ClassSchema,
    capability: "CLASS_MANAGE",
  }),
  "POST /api/v1/classes/:classId/reactivate": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: EmptyObjectSchema,
    response: ClassSchema,
    capability: "CLASS_MANAGE",
  }),
  "GET /api/v1/classes/:classId/roster": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: RosterEnvelopeSchema,
    capability: "ROSTER_READ",
  }),
  "GET /api/v1/classes/:classId/roster.csv": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: z.string(),
    capability: "ROSTER_READ",
  }),
  "PATCH /api/v1/classes/:classId/students/:studentNumber": route({
    params: StudentParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: StudentAccountPatchRequestSchema,
    response: StudentAccountSchema,
    capability: "STUDENT_MANAGE",
  }),
  "POST /api/v1/classes/:classId/students/:studentNumber/reset-password": route(
    {
      params: StudentParamsSchema,
      query: EmptyObjectSchema,
      headers: CsrfHeadersSchema,
      body: EmptyObjectSchema,
      response: UndefinedSchema,
      capability: "STUDENT_MANAGE",
    },
  ),
  "GET /api/v1/gallery/posts": route({
    params: EmptyObjectSchema,
    query: GalleryPostListQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: GalleryPostPageSchema,
    capability: null,
  }),
  "GET /api/v1/gallery/posts/:postId/image": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: BinaryBodySchema,
    capability: null,
  }),
  "DELETE /api/v1/gallery/posts/:postId": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: UndefinedSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "POST /api/v1/gallery/posts/:postId/allow-again": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: ClassGalleryPostSchema,
    capability: null,
  }),
  "PUT /api/v1/gallery/posts/:postId/reactions/:kind": route({
    params: GalleryReactionParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: GalleryReactionStateSchema,
    capability: null,
  }),
  "DELETE /api/v1/gallery/posts/:postId/reactions/:kind": route({
    params: GalleryReactionParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: UndefinedSchema,
    response: GalleryReactionStateSchema,
    capability: null,
  }),
  "POST /api/v1/gallery/posts/:postId/reports": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: GalleryReportRequestSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "GET /api/v1/gallery/schools": route({
    params: EmptyObjectSchema,
    query: SchoolSearchQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: GallerySchoolListSchema,
    capability: null,
  }),
  "GET /api/v1/gallery/schools/:schoolCode/classes": route({
    params: GallerySchoolParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: GalleryClassListSchema,
    capability: null,
  }),
  "PUT /api/v1/classes/:classId/canvases/:canvasId/frames/:frameId/gallery-post":
    route({
      params: FrameGalleryParamsSchema,
      query: EmptyObjectSchema,
      headers: GalleryImageUploadHeadersSchema,
      body: BinaryBodySchema,
      response: ClassGalleryPostSchema,
      capability: null,
    }),
  "GET /api/v1/classes/:classId/canvases/:canvasId/gallery-posts": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: CanvasGalleryPostListSchema,
    capability: null,
  }),
  "GET /api/v1/classes/:classId/gallery-posts": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: TeacherClassGalleryListSchema,
    capability: null,
  }),
  "POST /api/v1/classes/:classId/gallery-posts/seen": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: UndefinedSchema,
    capability: null,
  }),
  "GET /api/v1/admin/gallery-posts": route({
    params: EmptyObjectSchema,
    query: AdminGalleryPostListQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: AdminGalleryPostPageSchema,
    capability: null,
  }),
  "POST /api/v1/admin/gallery-posts/:postId/restore": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: AdminGalleryPostSchema,
    capability: null,
  }),
  "POST /api/v1/admin/gallery-posts/:postId/delete": route({
    params: GalleryPostParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: EmptyObjectSchema,
    response: AdminGalleryPostSchema,
    capability: null,
  }),
  "PUT /api/v1/me/school": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: SchoolSetRequestSchema,
    response: SchoolSchema,
    capability: null,
  }),
  "PUT /api/v1/me/display-name": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: DisplayNameRequestSchema,
    response: StudentAccountSchema,
    capability: null,
  }),
  "GET /api/v1/classes/:classId/canvases": route({
    params: ClassParamsSchema,
    query: CursorQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: CanvasPageSchema,
    capability: "CLASS_READ",
  }),
  "POST /api/v1/classes/:classId/canvases": route({
    params: ClassParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: CanvasCreateRequestSchema,
    response: CanvasSchema,
    capability: "CANVAS_CREATE",
  }),
  "GET /api/v1/classes/:classId/canvases/:canvasId": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: CanvasSchema,
    capability: "CANVAS_READ",
  }),
  "PATCH /api/v1/classes/:classId/canvases/:canvasId": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: CanvasRenameRequestSchema,
    response: CanvasSchema,
    capability: "CANVAS_MANAGE",
  }),
  "GET /api/v1/classes/:classId/canvases/trash": route({
    params: ClassParamsSchema,
    query: CursorQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: CanvasPageSchema,
    capability: "CLASS_MANAGE",
  }),
  "POST /api/v1/classes/:classId/canvases/:canvasId/trash": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: EmptyObjectSchema,
    response: CanvasSchema,
    capability: "CANVAS_MANAGE",
  }),
  "POST /api/v1/classes/:classId/canvases/:canvasId/restore": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: EmptyObjectSchema,
    response: CanvasSchema,
    capability: "CANVAS_MANAGE",
  }),
  "POST /api/v1/classes/:classId/canvases/:canvasId/duplicate": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: CanvasDuplicateRequestSchema,
    response: CanvasSchema,
    capability: "CANVAS_MANAGE",
  }),
  // Presentation lessons. Authorization is role-specific and done by the
  // service: the students list is the class's teacher's; opening a student's
  // canvas is that teacher's or that student's; completion is the owning
  // student's alone.
  "GET /api/v1/classes/:classId/presentations/:canvasId/students": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: PresentationStudentListSchema,
    capability: null,
  }),
  "POST /api/v1/classes/:classId/presentations/:canvasId/students/:studentNumber/canvas":
    route({
      params: PresentationStudentParamsSchema,
      query: EmptyObjectSchema,
      headers: CsrfHeadersSchema,
      body: EmptyObjectSchema,
      response: CanvasSchema,
      capability: null,
    }),
  "PUT /api/v1/classes/:classId/canvases/:canvasId/completion": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: CsrfHeadersSchema,
    body: CanvasCompletionRequestSchema,
    response: CanvasSchema,
    capability: null,
  }),
  "GET /api/v1/classes/:classId/canvases/:canvasId/state": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: CanvasStateSchema,
    capability: "CANVAS_READ",
  }),
  "PUT /api/v1/classes/:classId/canvases/:canvasId/assets/:fileId": route({
    params: AssetParamsSchema,
    query: EmptyObjectSchema,
    headers: AssetUploadHeadersSchema,
    body: BinaryBodySchema,
    response: AssetSchema,
    capability: "ASSET_WRITE",
  }),
  "GET /api/v1/classes/:classId/canvases/:canvasId/assets/:fileId": route({
    params: AssetParamsSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: BinaryBodySchema,
    capability: "ASSET_READ",
  }),
  "GET /api/v1/classes/:classId/canvases/:canvasId/revisions": route({
    params: CanvasParamsSchema,
    query: CursorQuerySchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: RevisionPageSchema,
    capability: "REVISION_READ",
  }),
  "POST /api/v1/classes/:classId/canvases/:canvasId/revisions": route({
    params: CanvasParamsSchema,
    query: EmptyObjectSchema,
    headers: IdempotencyHeadersSchema,
    body: EmptyObjectSchema,
    response: RevisionSchema,
    capability: "REVISION_RESTORE",
  }),
  "GET /api/v1/classes/:classId/canvases/:canvasId/revisions/:revisionId":
    route({
      params: RevisionParamsSchema,
      query: EmptyObjectSchema,
      headers: TransportHeadersSchema,
      body: UndefinedSchema,
      response: RevisionPreviewEnvelopeSchema,
      capability: "REVISION_READ",
    }),
  "POST /api/v1/classes/:classId/canvases/:canvasId/revisions/:revisionId/restore":
    route({
      params: RevisionParamsSchema,
      query: EmptyObjectSchema,
      headers: IdempotencyHeadersSchema,
      body: RevisionRestoreRequestSchema,
      response: RevisionRestoreEnvelopeSchema,
      capability: "REVISION_RESTORE",
    }),
  "GET /api/v1/health/live": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: HealthLiveEnvelopeSchema,
    capability: null,
  }),
  "GET /api/v1/health/ready": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: HealthReadyEnvelopeSchema,
    capability: null,
  }),
  "GET /metrics": route({
    params: EmptyObjectSchema,
    query: EmptyObjectSchema,
    headers: TransportHeadersSchema,
    body: UndefinedSchema,
    response: z.string(),
    capability: null,
  }),
} as const;

export type HttpRouteName = keyof typeof HttpRouteContracts;

type HttpResponseStatus =
  | 200
  | 201
  | 204
  | readonly [200, 201]
  | readonly [200, 503];

export const HTTP_STATUS_BY_ROUTE: Readonly<
  Record<HttpRouteName, HttpResponseStatus>
> = {
  "POST /api/v1/auth/login": 200,
  "GET /api/v1/auth/session": 200,
  "POST /api/v1/auth/session/refresh": 200,
  "POST /api/v1/auth/logout": 204,
  "POST /api/v1/auth/change-password": 204,
  "GET /api/v1/admin/teachers": 200,
  "POST /api/v1/admin/teachers": 201,
  "PATCH /api/v1/admin/teachers/:teacherId/status": 200,
  "POST /api/v1/admin/teachers/:teacherId/reset-password": 204,
  "POST /api/v1/admin/teachers/:teacherId/impersonate": 200,
  "POST /api/v1/auth/impersonation/end": 204,
  "GET /api/v1/auth/oauth/providers": 200,
  "POST /api/v1/email-verifications": 204,
  "GET /api/v1/schools": 200,
  "PUT /api/v1/admin/teachers/:teacherId/school": 200,
  "POST /api/v1/beta-applications": 201,
  "GET /api/v1/admin/beta-applications": 200,
  "POST /api/v1/admin/beta-applications/:applicationId/approve": 200,
  "POST /api/v1/admin/beta-applications/:applicationId/reject": 200,
  "POST /api/v1/admin/beta-applications/:applicationId/resend": 200,
  "DELETE /api/v1/admin/beta-applications/:applicationId": 204,
  "POST /api/v1/mail/unsubscribe": 200,
  "GET /api/v1/admin/overview": 200,
  "GET /api/v1/admin/teacher-activity": 200,
  "GET /api/v1/admin/live": 200,
  "GET /api/v1/admin/audit-events": 200,
  "GET /api/v1/admin/client-reports": 200,
  "GET /api/v1/classes": 200,
  "POST /api/v1/classes": 201,
  "GET /api/v1/classes/:classId": 200,
  "PATCH /api/v1/classes/:classId": 200,
  "POST /api/v1/classes/:classId/archive": 200,
  "POST /api/v1/classes/:classId/reactivate": 200,
  "GET /api/v1/classes/:classId/roster": 200,
  "GET /api/v1/classes/:classId/roster.csv": 200,
  "PATCH /api/v1/classes/:classId/students/:studentNumber": 200,
  "POST /api/v1/classes/:classId/students/:studentNumber/reset-password": 204,
  "GET /api/v1/gallery/posts": 200,
  "GET /api/v1/gallery/posts/:postId/image": 200,
  "DELETE /api/v1/gallery/posts/:postId": 204,
  "POST /api/v1/gallery/posts/:postId/allow-again": 200,
  "PUT /api/v1/gallery/posts/:postId/reactions/:kind": 200,
  "DELETE /api/v1/gallery/posts/:postId/reactions/:kind": 200,
  "POST /api/v1/gallery/posts/:postId/reports": 204,
  "GET /api/v1/gallery/schools": 200,
  "GET /api/v1/gallery/schools/:schoolCode/classes": 200,
  "PUT /api/v1/classes/:classId/canvases/:canvasId/frames/:frameId/gallery-post":
    [200, 201],
  "GET /api/v1/classes/:classId/canvases/:canvasId/gallery-posts": 200,
  "GET /api/v1/classes/:classId/gallery-posts": 200,
  "POST /api/v1/classes/:classId/gallery-posts/seen": 204,
  "GET /api/v1/admin/gallery-posts": 200,
  "POST /api/v1/admin/gallery-posts/:postId/restore": 200,
  "POST /api/v1/admin/gallery-posts/:postId/delete": 200,
  "PUT /api/v1/me/school": 200,
  "PUT /api/v1/me/display-name": 200,
  "GET /api/v1/classes/:classId/canvases": 200,
  "POST /api/v1/classes/:classId/canvases": 201,
  "GET /api/v1/classes/:classId/canvases/:canvasId": 200,
  "PATCH /api/v1/classes/:classId/canvases/:canvasId": 200,
  "GET /api/v1/classes/:classId/canvases/trash": 200,
  "POST /api/v1/classes/:classId/canvases/:canvasId/trash": 200,
  "POST /api/v1/classes/:classId/canvases/:canvasId/restore": 200,
  "POST /api/v1/classes/:classId/canvases/:canvasId/duplicate": 201,
  "GET /api/v1/classes/:classId/presentations/:canvasId/students": 200,
  "POST /api/v1/classes/:classId/presentations/:canvasId/students/:studentNumber/canvas":
    [200, 201],
  "PUT /api/v1/classes/:classId/canvases/:canvasId/completion": 200,
  "GET /api/v1/classes/:classId/canvases/:canvasId/state": 200,
  "PUT /api/v1/classes/:classId/canvases/:canvasId/assets/:fileId": [200, 201],
  "GET /api/v1/classes/:classId/canvases/:canvasId/assets/:fileId": 200,
  "GET /api/v1/classes/:classId/canvases/:canvasId/revisions": 200,
  "POST /api/v1/classes/:classId/canvases/:canvasId/revisions": 201,
  "GET /api/v1/classes/:classId/canvases/:canvasId/revisions/:revisionId": 200,
  "POST /api/v1/classes/:classId/canvases/:canvasId/revisions/:revisionId/restore": 200,
  "GET /api/v1/health/live": 200,
  "GET /api/v1/health/ready": [200, 503],
  "GET /metrics": 200,
};

type HttpCapability = ClassCapability | CanvasCapability;

export const HTTP_ROUTE_CAPABILITIES = Object.fromEntries(
  Object.entries(HttpRouteContracts)
    .filter(([, contract]) => Boolean(contract.capability))
    .map(([name, contract]) => [name, contract.capability]),
) as Readonly<Partial<Record<HttpRouteName, HttpCapability>>>;

export type HttpErrorEnvelope = z.infer<typeof HttpErrorEnvelopeSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;
export type TeacherCreateRequest = z.infer<typeof TeacherCreateRequestSchema>;
export type TeacherStatusRequest = z.infer<typeof TeacherStatusRequestSchema>;
export type ClassCreateRequest = z.infer<typeof ClassCreateRequestSchema>;
export type ClassRenameRequest = z.infer<typeof ClassRenameRequestSchema>;
export type UpdateGroupCountRequest = z.infer<
  typeof UpdateGroupCountRequestSchema
>;
export type StudentAccountPatchRequest = z.infer<
  typeof StudentAccountPatchRequestSchema
>;
export type DisplayNameRequest = z.infer<typeof DisplayNameRequestSchema>;
export type CanvasCreateRequest = z.infer<typeof CanvasCreateRequestSchema>;
export type CanvasRenameRequest = z.infer<typeof CanvasRenameRequestSchema>;
export type CanvasDuplicateRequest = z.infer<
  typeof CanvasDuplicateRequestSchema
>;
export type RevisionRestoreRequest = z.infer<
  typeof RevisionRestoreRequestSchema
>;
export type StudentAccount = z.infer<typeof StudentAccountSchema>;
export type Teacher = z.infer<typeof TeacherSchema>;
export type School = z.infer<typeof SchoolSchema>;
export type GalleryReactionKind = z.infer<typeof GalleryReactionKindSchema>;
export type GalleryPostStatus = z.infer<typeof GalleryPostStatusSchema>;
export type GalleryReportReason = z.infer<typeof GalleryReportReasonSchema>;
export type GalleryReactionCounts = z.infer<typeof GalleryReactionCountsSchema>;
export type GalleryReactionState = z.infer<typeof GalleryReactionStateSchema>;
export type GalleryPostCard = z.infer<typeof GalleryPostCardSchema>;
export type GalleryPostListQuery = z.input<typeof GalleryPostListQuerySchema>;
export type GalleryPostPage = z.infer<typeof GalleryPostPageSchema>;
export type GallerySchoolList = z.infer<typeof GallerySchoolListSchema>;
export type GalleryClassList = z.infer<typeof GalleryClassListSchema>;
export type ClassGalleryPost = z.infer<typeof ClassGalleryPostSchema>;
export type CanvasGalleryPostList = z.infer<typeof CanvasGalleryPostListSchema>;
export type TeacherClassGalleryPost = z.infer<
  typeof TeacherClassGalleryPostSchema
>;
export type TeacherClassGalleryList = z.infer<
  typeof TeacherClassGalleryListSchema
>;
export type AdminGalleryPost = z.infer<typeof AdminGalleryPostSchema>;
export type AdminGalleryPostPage = z.infer<typeof AdminGalleryPostPageSchema>;
export type SchoolSearchResponse = z.infer<typeof SchoolSearchResponseSchema>;
export type SchoolSetRequest = z.infer<typeof SchoolSetRequestSchema>;
export type ClassRecord = z.infer<typeof ClassSchema>;
export type CanvasRecord = z.infer<typeof CanvasSchema>;
export type CanvasCompletionRequest = z.infer<
  typeof CanvasCompletionRequestSchema
>;
export type PresentationStudent = z.infer<typeof PresentationStudentSchema>;
export type PresentationStudentList = z.infer<
  typeof PresentationStudentListSchema
>;
export type CanvasState = z.infer<typeof CanvasStateSchema>;
export type Asset = z.infer<typeof AssetSchema>;
export type AuthSessionEnvelope = z.infer<typeof AuthSessionEnvelopeSchema>;
export type SessionRefreshEnvelope = z.infer<
  typeof SessionRefreshEnvelopeSchema
>;
export type Revision = z.infer<typeof RevisionSchema>;
export type RevisionPreviewEnvelope = z.infer<
  typeof RevisionPreviewEnvelopeSchema
>;
export type RevisionRestoreEnvelope = z.infer<
  typeof RevisionRestoreEnvelopeSchema
>;
